import React, { useEffect, useMemo, useState } from "react";
import { allUsers } from "../actions/allUsers";
import { ENABLE_KEYS } from "../constants/FeatureFlags";

const PieChart = ({ data, size = 260 }) => {
  const total = data.reduce((sum, d) => sum + d.value, 0) || 1;
  let cumulative = 0;
  const radius = size / 2;
  const center = size / 2;

  const toCoord = (angle) => {
    const rad = (angle - 90) * (Math.PI / 180);
    return [center + radius * Math.cos(rad), center + radius * Math.sin(rad)];
  };

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={center} cy={center} r={radius} fill="#f3f4f6" />
      {data.map((d, i) => {
        const startAngle = (cumulative / total) * 360;
        cumulative += d.value;
        const endAngle = (cumulative / total) * 360;
        const [x1, y1] = toCoord(startAngle);
        const [x2, y2] = toCoord(endAngle);
        const largeArc = endAngle - startAngle > 180 ? 1 : 0;
        const pathData = `M ${center} ${center} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
        const colors = [
          "#1e3a8a",
          "#f59e0b",
          "#10b981",
          "#ef4444",
          "#8b5cf6",
          "#14b8a6",
          "#f43f5e",
        ];
        return <path key={i} d={pathData} fill={colors[i % colors.length]} />;
      })}
    </svg>
  );
};

const Keys = () => {
  const [users, setUsers] = useState([]);
  const TOTAL_KEYS = 1000000000;

  useEffect(() => {
    if (!ENABLE_KEYS) return;
    allUsers(setUsers);
  }, []);

  const founderUser = useMemo(
    () => users.find((u) => u.user_id === 16),
    [users],
  );

  const founderLabel = useMemo(() => {
    if (!founderUser) return "Founder";
    const name = `${founderUser.legal_first_name || ""} ${
      founderUser.legal_last_name || ""
    }`.trim();
    const brokerage = founderUser.brokerage_name
      ? ` • ${founderUser.brokerage_name}`
      : "";
    return `${name || "Founder"}${brokerage}`;
  }, [founderUser]);

  const baseRows = useMemo(() => {
    const founderRow = {
      label: founderLabel,
      user_id: founderUser?.user_id || 16,
      active: 950_000_000,
      staked: 0,
    };

    const treasuryRow = {
      label: "Treasury",
      user_id: null,
      active: 50_000_000,
      staked: 0,
    };

    const otherHolders = users
      .filter((u) => u.user_id !== founderUser?.user_id)
      .map((u) => ({
        label:
          `${u.legal_first_name || ""} ${u.legal_last_name || ""}`.trim() ||
          `User #${u.user_id}`,
        user_id: u.user_id,
        active: Number(u.key_balance) || 0,
        staked: Number(u.staked_keys) || 0,
      }))
      .sort((a, b) => b.active + b.staked - (a.active + a.staked))
      .slice(0, 8);

    return [founderRow, treasuryRow, ...otherHolders]
      .map((row) => ({ ...row, total: row.active + row.staked }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [users, founderUser, founderLabel]);

  const chartData = useMemo(
    () => [
      {
        label: baseRows[0]?.label || "Founder",
        value: baseRows[0]?.total || 0,
      },
      {
        label: "Treasury",
        value: baseRows.find((r) => r.label === "Treasury")?.total || 0,
      },
    ],
    [baseRows],
  );

  const percent = (val) => ((val / TOTAL_KEYS) * 100).toFixed(2);

  if (!ENABLE_KEYS) {
    return (
      <div className="w-[100%] tablet:w-[80%] mx-auto rounded-10 p-4 py-24 flex flex-col gap-6 my-36 text-center">
        <h1 className="font-bold text-36">Key Distribution</h1>
        <p className="text-19 text-gray-700">Keys are currently disabled.</p>
      </div>
    );
  }

  return (
    <div className="w-[100%] tablet:w-[90%] mx-auto rounded-10 p-6 py-24 flex flex-col gap-12 my-36">
      <div className="flex flex-col gap-8">
        <h1 className="font-bold text-52 text-center">Key Distribution</h1>
        <p className="text-19 leading-relaxed text-center max-w-4xl mx-auto">
          Current breakdown of the 1,000,000,000 total keys: 950,000,000 to the
          founder account and 50,000,000 held in Treasury. Staking is planned,
          so totals already include Actively Held + Staked.
        </p>

        {/* Pie + Summary */}
        <div className="flex flex-wrap gap-8 items-start justify-center">
          <div className="p-4 border rounded-10 bg-white shadow">
            <PieChart data={chartData} />
            <p className="text-center mt-2 text-sm text-gray-600">
              Total Supply: {TOTAL_KEYS.toLocaleString()} Keys
            </p>
          </div>

          {/* Top Holders Table */}
          <div className="flex-1 p-4 border rounded-10 bg-white shadow min-w-[360px]">
            <h2 className="font-bold text-lg mb-3">Top 10 Key Holders</h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2">Key Holder</th>
                  <th className="py-2 text-right">Actively Held</th>
                  <th className="py-2 text-right">Staked</th>
                  <th className="py-2 text-right">Total Keys</th>
                </tr>
              </thead>
              <tbody>
                {baseRows.map((holder, idx) => {
                  const content = holder.user_id ? (
                    <a
                      href={`/user/${holder.user_id}`}
                      className="text-blue-600 hover:underline"
                    >
                      {holder.label}
                    </a>
                  ) : (
                    holder.label
                  );

                  return (
                    <tr key={idx} className="border-b last:border-b-0">
                      <td className="py-2 pr-2">{content}</td>
                      <td className="py-2 text-right">
                        {holder.active.toLocaleString()}
                      </td>
                      <td className="py-2 text-right">
                        {holder.staked.toLocaleString()}
                      </td>
                      <td className="py-2 text-right font-semibold">
                        {holder.total.toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Keys;
